import { EditorContent, useEditor } from "@tiptap/react";
import { useEffect } from "react";
import { tiptapExtensions, textToDoc, TRUSTED_IMAGE_DOMAINS } from "@shared/tiptap-doc";

interface RichTextEditorProps {
  /** Initial tiptap JSON string, or null for empty. */
  valueDoc: string | null;
  /** Plaintext seed when valueDoc is null (legacy edit). */
  seedText?: string | null;
  /** Fires with the current doc as a JSON string on every change. */
  onChange: (docJson: string) => void;
  placeholder?: string;
  ariaLabel?: string;
}

const ALLOWED = [...TRUSTED_IMAGE_DOMAINS, "ibb.co"];
function hostTrusted(src: string): boolean {
  try {
    const host = new URL(src).host.toLowerCase();
    return ALLOWED.some((d) => host === d || host.endsWith("." + d));
  } catch {
    return false;
  }
}

/** Strip <img> from non-trusted hosts in pasted HTML (UX mirror of the server
 * rule; the server re-validates every write regardless). */
function scrubPastedHTML(html: string): string {
  return html.replace(/<img[^>]*>/gi, (tag) => {
    const m = tag.match(/src\s*=\s*["']([^"']+)["']/i);
    return m && hostTrusted(m[1]) ? tag : "";
  });
}

/** Rich-text editor over the shared tiptap schema, with a minimal toolbar. WarEra
 * articles paste through tiptap's HTML paste; non-trusted images are scrubbed. */
export function RichTextEditor({ valueDoc, seedText, onChange, placeholder, ariaLabel }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: tiptapExtensions,
    content: valueDoc ? JSON.parse(valueDoc) : textToDoc(seedText ?? ""),
    editorProps: {
      transformPastedHTML: scrubPastedHTML,
      attributes: {
        class: "prose prose-invert prose-sm max-w-none focus:outline-none min-h-[8rem] px-3 py-2",
        ...(ariaLabel ? { "aria-label": ariaLabel } : {}),
      },
    },
    onUpdate: ({ editor }) => onChange(JSON.stringify(editor.getJSON())),
  });
  useEffect(() => () => editor?.destroy(), [editor]);
  if (!editor) return null;

  const Btn = ({ on, active, label }: { on: () => void; active: boolean; label: string }) => (
    <button
      type="button"
      aria-pressed={active}
      onMouseDown={(e) => {
        e.preventDefault();
        on();
      }}
      className={`px-2 py-1 text-xs rounded ${active ? "bg-accent text-black" : "bg-panel2 text-muted hover:text-ink"}`}
    >
      {label}
    </button>
  );

  return (
    <div className="rounded-lg border border-border bg-panel2/40" aria-label={placeholder}>
      <div className="flex flex-wrap gap-1 border-b border-border p-1">
        <Btn label="B" active={editor.isActive("bold")} on={() => editor.chain().focus().toggleBold().run()} />
        <Btn label="i" active={editor.isActive("italic")} on={() => editor.chain().focus().toggleItalic().run()} />
        <Btn label="H2" active={editor.isActive("heading", { level: 2 })} on={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
        <Btn label="H3" active={editor.isActive("heading", { level: 3 })} on={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} />
        <Btn label="• List" active={editor.isActive("bulletList")} on={() => editor.chain().focus().toggleBulletList().run()} />
        <Btn label="1. List" active={editor.isActive("orderedList")} on={() => editor.chain().focus().toggleOrderedList().run()} />
        <Btn label="❝" active={editor.isActive("blockquote")} on={() => editor.chain().focus().toggleBlockquote().run()} />
        <Btn
          label="Link"
          active={editor.isActive("link")}
          on={() => {
            const url = window.prompt("Link URL (http/https)") ?? "";
            if (/^https?:\/\//i.test(url)) editor.chain().focus().setLink({ href: url }).run();
            else editor.chain().focus().unsetLink().run();
          }}
        />
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
